// components/Toolbar.js
import React, { useState, useMemo } from 'react';
import './Toolbar.css';
import ResultsTable from './ResultsTable';
import EquivalencyPanel from './equivalencies/EquivalencyPanel';

/**
 * Toolbar
 * - Collects user inputs (passed in via `quantities`)
 * - Builds the calculation payload for ResultsTable (converts lbs -> tons)
 * - Receives grand totals back from ResultsTable via `onTotalsChange`
 * - Passes those totals to the EquivalencyPanel so "Based on ..." is dynamic
 */
const Toolbar = ({ quantities }) => {
  const [calculationResults, setCalculationResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [totals, setTotals] = useState({ mtco2e: 0, mmbtu: 0 });

  // Do we have any inputs > 0?
  const hasQuantities = useMemo(
    () =>
      Object.values(quantities).some(group =>
        Object.values(group || {}).some(v => Number(v) > 0)
      ),
    [quantities]
  );

  // Build the `results` structure expected by ResultsTable
  // - Convert pounds to tons (WARM factors are per ton)
  const handleCalculate = () => {
    if (!hasQuantities) return;

    const results = {};
    Object.entries(quantities).forEach(([materialType, materials]) => {
      const group = {};
      Object.entries(materials || {}).forEach(([material, inputLbs]) => {
        const lbs = Number(inputLbs) || 0;
        if (lbs <= 0) return;
        const tons = lbs / 2000;
        group[material] = { inputLbs: lbs, inputTons: tons };
      });
      if (Object.keys(group).length) results[materialType] = group;
    });

    setCalculationResults(results);
    setShowResults(true);
  };

  // Only pass totals to the EquivalencyPanel when the table is visible
  const calculatedTotals = useMemo(() => {
    if (!showResults) return null;
    return totals;
  }, [showResults, totals]);

  return (
    <>
      <div className="toolbar">
        <div className="toolbar-content">
          <div className="toolbar-info">
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className="info-value">
                {hasQuantities ? 'Ready to calculate' : 'Enter material quantities above'}
              </span>
            </div>
          </div>

          <button
            className="calculate-button"
            onClick={handleCalculate}
            disabled={!hasQuantities}
          >
            <span className="button-icon">🧮</span>
            Calculate!
          </button>
        </div>
      </div>

      <ResultsTable
        results={calculationResults}
        isVisible={showResults}
        // Receive grand totals from ResultsTable here:
        onTotalsChange={setTotals}
        // Render the equivalencies panel directly under the table:
        postTableSlot={
          calculatedTotals && <EquivalencyPanel totals={calculatedTotals} />
        }
      />
    </>
  );
};

export default Toolbar;
